import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import {
	getContractRequiredMinimumRefinement,
	refineContractRequiredMinimum,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { getManualValueShape } from "web_myplann/Portfolio/PortfolioShared/getManualValueShape";
import { refineManualValue } from "web_myplann/Portfolio/PortfolioShared/refineManualValue";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import {
	type KeyFieldsLockOptions,
	lockableField,
} from "web_myplann/Shared/Validation/lockableField";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { refineDateOrder } from "web_myplann/Shared/Validation/refineDateOrder";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const getPensionBaseShape = (
	t: TranslateFunction,
	{ lockKeyFields = false }: KeyFieldsLockOptions = {},
) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return {
		name: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
		),
		contractNumber: lockableField(
			trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			lockKeyFields,
		),
		producer: lockableField(
			trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			lockKeyFields,
		),
		product: lockableField(
			trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			lockKeyFields,
		),
		signedAt: lockableField(
			optionalDateAsString(DateFormat.Iso),
			lockKeyFields,
		),
		startAt: lockableField(optionalDateAsString(DateFormat.Iso), lockKeyFields),
		endAt: lockableField(optionalDateAsString(DateFormat.Iso), lockKeyFields),
		payment: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		paymentFrequency: z.nativeEnum(ClientPortfolioPaymentFrequencyEnum),
		employerContribution: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		ageLimit: optionalPositiveInteger(
			ValidationLimits.Age.Max,
			positiveNumberMessages,
		),
		appreciation: optionalPositiveFloat(
			ValidationLimits.Percent.Max,
			positiveNumberMessages,
		),
	};
};

const getDateOrderRefinement = (t: TranslateFunction) =>
	refineDateOrder({
		endBeforeSigned: t(
			Resources.Portfolio.AddShared.Validations.endBeforeSigned,
		),
	});

export const getPensionCreateFormSchema = (t: TranslateFunction) =>
	z
		.object({
			...getPensionBaseShape(t),
			...getManualValueShape(t),
		})
		.superRefine(refineManualValue(t))
		.superRefine(getDateOrderRefinement(t))
		.superRefine(refineContractRequiredMinimum);

export const getPensionEditFormSchema = (
	t: TranslateFunction,
	options: KeyFieldsLockOptions = {},
) =>
	z
		.object(getPensionBaseShape(t, options))
		.superRefine(getDateOrderRefinement(t))
		.superRefine(getContractRequiredMinimumRefinement(options));

export type PensionCreateFormInput = z.input<
	ReturnType<typeof getPensionCreateFormSchema>
>;
export type PensionCreateFormOutput = z.output<
	ReturnType<typeof getPensionCreateFormSchema>
>;

export type PensionEditFormInput = z.input<
	ReturnType<typeof getPensionEditFormSchema>
>;
export type PensionEditFormOutput = z.output<
	ReturnType<typeof getPensionEditFormSchema>
>;
